// Custom hooks únicos - elimina duplicação de lógica
import { useState, useEffect, useCallback, useRef } from 'react';
import { User, type User as UserType } from "@/entities/User";
import { debounce } from './shared';

// Hook único para carregamento de usuário
export const useUser = () => {
  const [user, setUser] = useState<UserType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef(true);

  const loadUser = useCallback(async () => {
    setLoading(true);
    try {
      const currentUser = await User.me();
      if (mountedRef.current) {
        setUser(currentUser);
        setError(null);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err as Error);
        setUser(null);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadUser();
    return () => { mountedRef.current = false; };
  }, [loadUser]);

  return { user, loading, error, reload: loadUser };
};

// Hook único para data fetching com debounce
export const useAsyncData = (fetchFn: () => Promise<any>, dependencies: any[] = [], debounceMs = 200) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef(true);

  const debouncedFetch = useCallback(
    debounce(async () => {
      if (!mountedRef.current) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const result = await fetchFn();
        if (mountedRef.current) {
          setData(result);
        }
      } catch (err) {
        if (mountedRef.current) {
          setError(err as Error);
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    }, debounceMs),
    [fetchFn, debounceMs]
  );

  useEffect(() => {
    debouncedFetch();
  }, dependencies);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return { data, loading, error, refetch: debouncedFetch };
};

// Hook único para localStorage
export const useLocalStorage = (key: string, defaultValue: any) => {
  const [value, setValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const setStoredValue = useCallback((newValue: any) => {
    try {
      setValue(newValue);
      window.localStorage.setItem(key, JSON.stringify(newValue));
    } catch (error) {
      console.error(`Error saving to localStorage:`, error);
    }
  }, [key]);

  return [value, setStoredValue];
};

// Hook único para form handling
export const useForm = (initialValues: any, validationFn?: (values: any) => any) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = useCallback((field: string, value: any) => {
    setValues((prev: any) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev: Record<string, any>) => ({ ...prev, [field]: null }));
    }
  }, [errors]);

  const validate = useCallback(() => {
    if (!validationFn) return true;
    
    const newErrors = validationFn(values);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [values, validationFn]);

  const handleSubmit = useCallback(async (onSubmit: (values: any) => void) => {
    if (!validate()) return;
    
    setIsSubmitting(true);
    try {
      await onSubmit(values);
    } finally {
      setIsSubmitting(false);
    }
  }, [values, validate]);

  return {
    values,
    errors,
    isSubmitting,
    handleChange,
    handleSubmit,
    setValues
  };
};
